import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const TemperatureGraphs = ({ apiData }) => {
  if (!apiData?.data) {
    return <div className="text-center p-4">No temperature data available</div>;
  }

  const { leftSide, rightSide, metadata } = apiData.data;
  const labels = metadata.map(item => `Axle ${item.axleNo}`);

  // Define all possible datasets
  const allDatasets = [
    {
      label: 'LH Axle',
      data: leftSide.axle.map(item => item.value ?? null), // Convert undefined to null
      borderColor: '#FF6384',
      backgroundColor: 'rgba(255, 99, 132, 0.2)',
      tension: 0.3,
    },
    {
      label: 'RH Axle',
      data: rightSide.axle.map(item => item.value ?? null),
      borderColor: '#FF9F40',
      backgroundColor: 'rgba(255, 159, 64, 0.2)',
      tension: 0.3,
    },
    {
      label: 'LH Wheel',
      data: leftSide.wheel.map(item => item.value ?? null),
      borderColor: '#36A2EB',
      backgroundColor: 'rgba(54, 162, 235, 0.2)',
      tension: 0.3,
    },
    {
      label: 'RH Wheel',
      data: rightSide.wheel.map(item => item.value ?? null),
      borderColor: '#9966FF',
      backgroundColor: 'rgba(153, 102, 255, 0.2)',
      tension: 0.3,
    },
    {
      label: 'LH Brake Disc',
      data: leftSide.brakeDisc.map(item => item.value ?? null),
      borderColor: '#4BC0C0',
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
      tension: 0.3,
    },
    {
      label: 'RH Brake Disc',
      data: rightSide.brakeDisc.map(item => item.value ?? null),
      borderColor: '#FFCE56',
      backgroundColor: 'rgba(255, 206, 86, 0.2)',
      tension: 0.3,
    },
  ];

  // Filter datasets: hide if all values are 0, null, or negative
  const datasets = allDatasets.map(dataset => {
    const allZeroNullOrNegative = dataset.data.every(value => value === null || value <= 0);
    return {
      ...dataset,
      hidden: allZeroNullOrNegative, // Hide the line by default if all values are 0, null, or negative
    };
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true, // Use point style for legend
        },
        onClick: (e, legendItem, legend) => {
          const index = legendItem.datasetIndex;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(index);
          
          // Toggle visibility: If the dataset is currently hidden, show it; otherwise, hide it
          meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;

          // Update the chart to reflect the change
          chart.update();
        },
      },
      title: {
        display: true,
        text: 'Temperature Distribution Across Axles',
        font: { size: 16 },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y}°C`,
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Axle Number',
          font: { size: 14 },
        },
      },
      y: {
        title: {
          display: true,
          text: 'Temperature (°C)',
          font: { size: 14 },
        },
        beginAtZero: true,
      },
    },
  };

  const chartData = {
    labels,
    datasets,
  };

  return (
    <div className="w-full">
      <div className="bg-white p-4 rounded-lg shadow-md">
        {/* <h2 className="text-xl font-bold mb-4 text-center">Temperature Distribution</h2> */}
        <div className="h-96">
          <Line options={chartOptions} data={chartData} />
        </div>
      </div>
    </div>
  );
};

export default TemperatureGraphs;
